import { IPerson, Person } from './person';
import { Company, ICompany } from './company';
import { BcmOrderAttachment, BcmOrderAttachmentDto } from './bcm-order-attachment';
import { BcmOrderBoatWeighing, BcmOrderBoatWeighingDto } from './bcm-order-boat-weighing';
import { BcmOrderInvoicePosition, BcmOrderInvoicePositionDto } from './bcm-order-invoice-position';
import { BcmOrderStatus, BcmOrderStatusDto, BcmOrderStatusType, bcmOrderStatusTranslationKeys } from './bcm-order-status';
import { BcmOrderTodo, BcmOrderTodoDto } from './bcm-order-todo';
import { BcmNote, BcmNoteDto } from './bcm-note';
import { Boat, IBoat } from './boat';
import { tryParseDate } from '@shared/functions/try-parse-date';
import { isOrderBillingReady } from '@shared/helpers/order-overview-model.util';
import { addDays, isSameDay } from '@core/date.facade';
import { BcmReceipt, BcmReceiptDto } from '@shared/models/bcm-receipt';
import { User } from '@core/services/api/u2b-user.api-service';
import { OrderTaskPositionAssigneeType } from '@shared/helpers/order-task-position-assignee.util';
import { BcmEmployeeDto, BcmEmployee } from '@shared/models/bcm-employee';
import { BcmDepartmentDto, BcmDepartment } from '@shared/models/bcm-department';
import { resolveOrderAssigneeDisplayName } from '@shared/helpers/order-order-assignee.util';

export enum BcmOrderWorkPackageType {
    Crane = 'crane',
    Slip = 'slip',
    None = 'none'
}

export enum BcmOrderExportType {
    Invoice = 'invoice',
    CashReceipt = 'cash-receipt'
}

export interface BcmOrderSourceQuoteDto {
    id: number;
    quote_number?: string | null;
    version?: number | null;
    lifecycle_status?: string | null;
}

export interface BcmOrderDto {
    id: number;
    orderTitle: string;
    orderType: BcmOrderWorkPackageType;
    startTime: Date | string | null;
    endTime: Date | string | null;
    startedOn: Date | string | null;
    endedOn: Date | string | null;
    exportType: BcmOrderExportType;
    sendMailOnStatusChange: boolean; // future functionality
    lastStatus: BcmOrderStatusType;
    insertedOn: string;
    insertedBy: string;
    assignedUser: string;
    assignedUserObject: User;
    assigneeType?: OrderTaskPositionAssigneeType | null;
    bcm_employees_id?: number | null;
    bcm_departments_id?: number | null;
    assignedEmployee?: BcmEmployeeDto | null;
    assignedDepartment?: BcmDepartmentDto | null;
    lastUpdateOn: string;
    lastUpdateBy: string;
    tasks: string;

    client: IPerson;
    clientCompany: ICompany;
    boats: IBoat[];
    boatWeighings: BcmOrderBoatWeighingDto[];
    notes: BcmNoteDto[];
    note: string | null;
    status: BcmOrderStatusDto[];
    todos: BcmOrderTodoDto[];
    orderAttachments: BcmOrderAttachmentDto[];
    orderInvoicePositions: BcmOrderInvoicePositionDto[];

    invoiceNumber?: string;
    receipts: BcmReceiptDto[];
    cartTotal?: number;
    billingReady?: boolean | number;
    startAsSoonAsPossible?: boolean;
    sourceQuote?: BcmOrderSourceQuoteDto | null;
    boatPhotoCount?: number;
}

export class BcmOrder {
    id;
    orderNumber: string;
    orderTitle;
    orderType: BcmOrderWorkPackageType = BcmOrderWorkPackageType.None;
    startTime: Date;
    endTime;
    startedOn;
    endedOn;
    exportType: BcmOrderExportType = BcmOrderExportType.Invoice;
    sendMailOnStatusChange;
    lastStatus: BcmOrderStatusType;
    lastStatusName: string;
    tasks: string;
    assignedUser: string;
    assignedUserName: string;
    assignedUserObject: User;
    assigneeType: OrderTaskPositionAssigneeType | null;
    bcm_employees_id: number | null;
    bcm_departments_id: number | null;
    assignedEmployee: BcmEmployee | null;
    assignedDepartment: BcmDepartment | null;
    readonly insertedOn;
    readonly insertedBy;
    readonly lastUpdateOn;
    readonly lastUpdateBy;

    client?: Person | null = null;
    clientCompany?: Company | null = null;
    activeClient: Person | Company | null;
    boats: Boat[] = [];
    boatWeighings: BcmOrderBoatWeighing[] = [];
    note: string | null;
    notes: BcmNote[] = [];
    statusHistory: BcmOrderStatus[] = [];
    todos: BcmOrderTodo[] = [];
    orderAttachments: BcmOrderAttachment[] = [];
    orderInvoicePositions: BcmOrderInvoicePosition[] = [];

    // helpers
    isSameDayStartAndEnd: boolean;
    isToday: boolean;
    isTomorrow: boolean;
    clientName: string;
    userName: string;
    boatPhotoCount?: number;

    doneState: number;

    invoiceNumber?: string;
    receipts?: BcmReceipt[];
    cartTotal = 0;
    billingReady = false;
    billingReadyLabel: string;
    sourceQuote: BcmOrderSourceQuoteDto | null;

    constructor(bcmOrderDto: BcmOrderDto = {} as BcmOrderDto) {
        this.id = bcmOrderDto.id;
        this.orderNumber = bcmOrderDto.id ? ('#' + String(bcmOrderDto.id).padStart(4, '0')) : '';
        this.orderTitle = bcmOrderDto.orderTitle;
        this.orderType = bcmOrderDto.orderType;
        this.startTime = tryParseDate(bcmOrderDto.startTime);
        this.endTime = tryParseDate(bcmOrderDto.endTime);
        this.startedOn = tryParseDate(bcmOrderDto.startedOn);
        this.endedOn = tryParseDate(bcmOrderDto.endedOn);
        this.exportType = bcmOrderDto.exportType;
        this.sendMailOnStatusChange = bcmOrderDto.sendMailOnStatusChange;
        this.lastStatus = bcmOrderDto.lastStatus;
        this.lastStatusName = bcmOrderStatusTranslationKeys[bcmOrderDto.lastStatus] ?? bcmOrderStatusTranslationKeys[BcmOrderStatusType.Open];
        this.insertedOn = tryParseDate(bcmOrderDto.insertedOn);
        this.insertedBy = bcmOrderDto.insertedBy;
        this.assignedUser = bcmOrderDto.assignedUser;
        this.assigneeType = bcmOrderDto.assigneeType ?? null;
        this.bcm_employees_id = bcmOrderDto.bcm_employees_id ?? null;
        this.bcm_departments_id = bcmOrderDto.bcm_departments_id ?? null;
        this.assignedEmployee = bcmOrderDto.assignedEmployee
            ? new BcmEmployee(bcmOrderDto.assignedEmployee)
            : null;
        this.assignedDepartment = bcmOrderDto.assignedDepartment
            ? new BcmDepartment(bcmOrderDto.assignedDepartment)
            : null;

        this.lastUpdateOn = tryParseDate(bcmOrderDto.lastUpdateOn);
        this.lastUpdateBy = bcmOrderDto.lastUpdateBy;
        this.tasks = bcmOrderDto.tasks;

        this.isSameDayStartAndEnd = !!this.startTime && !!this.endTime && isSameDay(this.startTime, this.endTime);
        this.isToday = !!this.startTime && isSameDay(this.startTime, new Date());
        this.isTomorrow = !!this.startTime && isSameDay(this.startTime, addDays(new Date(), 1));

        this.client = bcmOrderDto.client ? new Person(bcmOrderDto.client) : null;
        this.clientCompany = bcmOrderDto.clientCompany ? new Company(bcmOrderDto.clientCompany) : null;
        this.boats = (bcmOrderDto.boats || []).map(boat => new Boat(boat));
        this.boatWeighings = (bcmOrderDto.boatWeighings || []).map(weighing => new BcmOrderBoatWeighing(weighing));
        this.notes = (bcmOrderDto.notes || []).map(note => new BcmNote(note));
        this.note = bcmOrderDto.note;
        this.statusHistory = (bcmOrderDto.status || []).map(status => new BcmOrderStatus(status));
        this.todos = (bcmOrderDto.todos || []).map(todo => new BcmOrderTodo(todo));
        this.orderAttachments = (bcmOrderDto.orderAttachments || []).map(orderAttachment => new BcmOrderAttachment(orderAttachment));
        this.orderInvoicePositions = (bcmOrderDto.orderInvoicePositions || []).map(orderInvoicePosition => new BcmOrderInvoicePosition(orderInvoicePosition));

        this.doneState = this.todos.filter(todo => todo.done).length / this.todos.length;

        this.activeClient = this.client || this.clientCompany || null;
        this.clientName = (this.activeClient)?.fullName;

        this.invoiceNumber = bcmOrderDto.invoiceNumber;
        this.receipts = (bcmOrderDto.receipts || []).map(r => new BcmReceipt(r));
        this.cartTotal = Number(bcmOrderDto.cartTotal ?? 0);
        this.billingReady = isOrderBillingReady(bcmOrderDto.lastStatus)
            || bcmOrderDto.billingReady === true
            || bcmOrderDto.billingReady === 1;
        this.billingReadyLabel = this.billingReady ? 'Ja' : 'Nein';
        this.sourceQuote = bcmOrderDto.sourceQuote ?? null;
        this.boatPhotoCount = Number(bcmOrderDto.boatPhotoCount ?? 0);

        this.assignedUserObject = bcmOrderDto.assignedUserObject;
        this.assignedUserName = resolveOrderAssigneeDisplayName({
            assigneeType: this.assigneeType,
            bcm_employees_id: this.bcm_employees_id,
            bcm_departments_id: this.bcm_departments_id,
            assignedEmployee: this.assignedEmployee,
            assignedDepartment: this.assignedDepartment,
            assignedUserObject: this.assignedUserObject,
        });

    }
}
